/**
 * One slide's form (plan docs/plans/lms-plan.md 8.1 "a slide list per
 * section with layout pickers and the markdown subset in textareas";
 * Appendix E for every layout's fields; 6.1, 6.2, 18.11; WP-44). The *_md
 * fields are block markdown, bullets and table cells inline markdown, every
 * other field plain text (18.11); the live preview beside it is the player's
 * own SlideView. knowledge_check and scenario slides keep their correct and
 * best flags (18.11), set here per option.
 */
import { useId } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { iconBtnCls, outlineBtnCls } from '../../../../ui/styles';
import type {
  CalloutTone,
  ChoiceOption,
  FigureKind,
  FigureSlide,
  KnowledgeCheckSlide,
  OptionId,
  QuestionKind,
  ScenarioOption,
  ScenarioSlide,
  Slide,
  SlideLayout,
  TableSlide,
} from '../../../content/types';
import { CALLOUT_TONES, FIGURE_KINDS, OPTION_IDS, QUESTION_KINDS, SLIDE_LAYOUTS } from '../../../content/types';
import { AssetPicker } from './AssetPicker';
import { RowActions, SelectField, StringList, TextArea, TextInput } from './fields';
import { blankOptions, blankSlide, LAYOUT_LABELS, moveItem, removeAt, replaceAt } from './model';
import { checkboxCls, hintCls, labelCls, microLabelCls, subheadingCls } from './styles';

const addBtnCls = `${outlineBtnCls()} disabled:cursor-not-allowed disabled:opacity-50`;

export const QUESTION_KIND_LABELS: Record<QuestionKind, string> = { single: 'One answer', multi: 'Several answers', true_false: 'True or false' };
const TONE_LABELS: Record<CalloutTone, string> = { rule: 'Rule (a must-do)', risk: 'Risk (a consequence)', tip: 'Tip' };
const FIGURE_LABELS: Record<FigureKind, string> = { flow: 'Flow', cycle: 'Cycle', compare: 'Compare', hierarchy: 'Hierarchy' };

/** 5.2: 2 to 6 options, ids a to f. */
const MAX_OPTIONS = OPTION_IDS.length;

function nextOptionId(ids: string[]): OptionId | null {
  return (OPTION_IDS.find((id) => !ids.includes(id)) as OptionId | undefined) ?? null;
}

export interface SlideFormProps {
  slide: Slide;
  onChange: (slide: Slide) => void;
}

// ---------------------------------------------------------------------------
// Options (knowledge check and scenario)
// ---------------------------------------------------------------------------

export function ChoiceOptionsEditor({
  options,
  kind,
  onChange,
  testId,
}: {
  options: ChoiceOption[];
  kind: QuestionKind;
  onChange: (options: ChoiceOption[]) => void;
  testId: string;
}) {
  const multi = kind === 'multi';
  const setCorrect = (index: number, checked: boolean) => {
    if (multi) onChange(options.map((o, i) => (i === index ? { ...o, correct: checked } : o)));
    else onChange(options.map((o, i) => ({ ...o, correct: i === index })));
  };
  return (
    <fieldset className="space-y-2" data-testid={testId}>
      <legend className={labelCls}>{multi ? 'Options (tick every correct one)' : 'Options (choose the correct one)'}</legend>
      {options.map((o, i) => (
        <div key={o.id} className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid={`${testId}-${o.id}`}>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              {o.id}
            </span>
            <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0">
              <input
                type={multi ? 'checkbox' : 'radio'}
                name={`${testId}-correct`}
                checked={o.correct === true}
                onChange={(e) => setCorrect(i, e.target.checked)}
                className={checkboxCls}
                data-testid={`${testId}-${o.id}-correct`}
              />
              Correct
            </label>
            <div className="ml-auto">
              <button
                type="button"
                className={iconBtnCls}
                disabled={options.length <= 2 || kind === 'true_false'}
                onClick={() => onChange(removeAt(options, i))}
                aria-label={`Remove option ${o.id}`}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          <TextInput label={`Option ${o.id}`} value={o.text} onChange={(text) => onChange(replaceAt(options, i, { ...o, text }))} testId={`${testId}-${o.id}-text`} />
          <TextInput
            label="Feedback (why this answer is right or wrong)"
            value={o.feedback}
            onChange={(feedback) => onChange(replaceAt(options, i, { ...o, feedback }))}
            testId={`${testId}-${o.id}-feedback`}
          />
        </div>
      ))}
      <button
        type="button"
        className={addBtnCls}
        disabled={options.length >= MAX_OPTIONS || kind === 'true_false'}
        onClick={() => {
          const id = nextOptionId(options.map((o) => o.id));
          if (id) onChange([...options, { id, text: '', correct: false, feedback: '' }]);
        }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add option
      </button>
    </fieldset>
  );
}

/** A question kind change keeps the rules of 5.2: true_false is True and False; single keeps one correct answer. */
export function optionsForKind(options: ChoiceOption[], kind: QuestionKind): ChoiceOption[] {
  if (kind === 'true_false') return blankOptions('true_false');
  if (kind === 'single') {
    const first = options.findIndex((o) => o.correct === true);
    return options.map((o, i) => ({ ...o, correct: i === (first < 0 ? 0 : first) }));
  }
  return options;
}

function ScenarioOptionsEditor({ options, onChange }: { options: ScenarioOption[]; onChange: (options: ScenarioOption[]) => void }) {
  return (
    <fieldset className="space-y-2" data-testid="lms-scenario-options">
      <legend className={labelCls}>Choices (mark the best one)</legend>
      {options.map((o, i) => (
        <div key={o.id} className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              {o.id}
            </span>
            <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0">
              <input
                type="radio"
                name="lms-scenario-best"
                checked={o.best === true}
                onChange={() => onChange(options.map((x, j) => ({ ...x, best: j === i })))}
                className={checkboxCls}
              />
              Best choice
            </label>
            <div className="ml-auto">
              <button
                type="button"
                className={iconBtnCls}
                disabled={options.length <= 2}
                onClick={() => onChange(removeAt(options, i))}
                aria-label={`Remove choice ${o.id}`}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
          <TextInput label={`Choice ${o.id}`} value={o.text} onChange={(text) => onChange(replaceAt(options, i, { ...o, text }))} />
          <TextInput label="Feedback (name the rule)" value={o.feedback} onChange={(feedback) => onChange(replaceAt(options, i, { ...o, feedback }))} />
        </div>
      ))}
      <button
        type="button"
        className={addBtnCls}
        disabled={options.length >= MAX_OPTIONS}
        onClick={() => {
          const id = nextOptionId(options.map((o) => o.id));
          if (id) onChange([...options, { id, text: '', best: false, feedback: '' }]);
        }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add choice
      </button>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Layout-specific parts
// ---------------------------------------------------------------------------

function TableEditor({ slide, onChange }: { slide: TableSlide; onChange: (slide: TableSlide) => void }) {
  const columns = Array.isArray(slide.columns) ? slide.columns : [];
  const rows = Array.isArray(slide.rows) ? slide.rows : [];
  const setCell = (r: number, c: number, value: string) =>
    onChange({ ...slide, rows: rows.map((row, i) => (i === r ? row.map((cell, j) => (j === c ? value : cell)) : row)) });
  return (
    <div className="space-y-3" data-testid="lms-table-editor">
      <div className={labelCls}>Columns and rows (inline markdown in each cell)</div>
      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-1">
          <thead>
            <tr>
              {columns.map((col, c) => (
                <th key={c} className="text-left">
                  <input
                    aria-label={`Column ${c + 1} heading`}
                    value={col}
                    onChange={(e) => onChange({ ...slide, columns: replaceAt(columns, c, e.target.value) })}
                    className="min-h-[44px] w-full min-w-[8rem] rounded-lg border border-gray-300 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0"
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r}>
                {columns.map((_, c) => (
                  <td key={c}>
                    <input
                      aria-label={`Row ${r + 1}, column ${c + 1}`}
                      value={row[c] ?? ''}
                      onChange={(e) => setCell(r, c, e.target.value)}
                      className="min-h-[44px] w-full min-w-[8rem] rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0"
                    />
                  </td>
                ))}
                <td>
                  <button type="button" className={iconBtnCls} onClick={() => onChange({ ...slide, rows: removeAt(rows, r) })} disabled={rows.length <= 1} aria-label={`Remove row ${r + 1}`}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={addBtnCls} onClick={() => onChange({ ...slide, rows: [...rows, columns.map(() => '')] })}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add row
        </button>
        <button
          type="button"
          className={addBtnCls}
          onClick={() => onChange({ ...slide, columns: [...columns, ''], rows: rows.map((row) => [...row, '']) })}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add column
        </button>
        <button
          type="button"
          className={addBtnCls}
          disabled={columns.length <= 1}
          onClick={() => onChange({ ...slide, columns: columns.slice(0, -1), rows: rows.map((row) => row.slice(0, columns.length - 1)) })}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          Remove last column
        </button>
      </div>
    </div>
  );
}

function FigureEditor({ slide, onChange }: { slide: FigureSlide; onChange: (slide: FigureSlide) => void }) {
  const figure = slide.figure;
  const nodes = Array.isArray(figure?.nodes) ? figure.nodes : [];
  const edges = Array.isArray(figure?.edges) ? figure.edges : [];
  const setFigure = (next: Partial<FigureSlide['figure']>) => onChange({ ...slide, figure: { ...figure, ...next } });
  const nodeOptions = nodes.map((n) => ({ value: n.id, label: n.label ? `${n.id}: ${n.label}` : n.id }));
  const nextNodeId = () => {
    let n = nodes.length + 1;
    const used = new Set(nodes.map((x) => x.id));
    while (used.has(`n${n}`)) n += 1;
    return `n${n}`;
  };
  return (
    <div className="space-y-3" data-testid="lms-figure-editor">
      <SelectField
        label="Figure kind"
        value={figure?.kind ?? 'flow'}
        onChange={(kind) => setFigure({ kind: kind as FigureKind })}
        options={FIGURE_KINDS.map((k) => ({ value: k, label: FIGURE_LABELS[k] }))}
      />
      <fieldset className="space-y-2">
        <legend className={labelCls}>Boxes</legend>
        {nodes.map((node, i) => (
          <div key={node.id} className="flex flex-wrap items-end gap-2">
            <span className={`${microLabelCls} w-8`}>{node.id}</span>
            <TextInput
              className="min-w-[10rem] flex-1"
              label={`Box ${node.id} label`}
              value={node.label}
              onChange={(label) => setFigure({ nodes: replaceAt(nodes, i, { ...node, label }) })}
            />
            <TextInput
              className="min-w-[10rem] flex-1"
              label={`Box ${node.id} note`}
              value={node.note ?? ''}
              onChange={(note) => setFigure({ nodes: replaceAt(nodes, i, { ...node, note: note || null }) })}
            />
            <button
              type="button"
              className={iconBtnCls}
              disabled={nodes.length <= 2}
              onClick={() =>
                setFigure({
                  nodes: removeAt(nodes, i),
                  edges: edges.filter((e) => e.from !== node.id && e.to !== node.id),
                })
              }
              aria-label={`Remove box ${node.id}`}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ))}
        <button type="button" className={addBtnCls} onClick={() => setFigure({ nodes: [...nodes, { id: nextNodeId(), label: '', note: null }] })}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add box
        </button>
      </fieldset>
      <fieldset className="space-y-2">
        <legend className={labelCls}>Arrows</legend>
        {edges.map((edge, i) => (
          <div key={i} className="flex flex-wrap items-end gap-2">
            <SelectField
              className="w-32"
              label="From"
              value={edge.from}
              onChange={(from) => setFigure({ edges: replaceAt(edges, i, { ...edge, from }) })}
              options={nodeOptions}
            />
            <SelectField
              className="w-32"
              label="To"
              value={edge.to}
              onChange={(to) => setFigure({ edges: replaceAt(edges, i, { ...edge, to }) })}
              options={nodeOptions}
            />
            <TextInput
              className="min-w-[10rem] flex-1"
              label="Arrow label"
              value={edge.label ?? ''}
              onChange={(label) => setFigure({ edges: replaceAt(edges, i, { ...edge, label: label || null }) })}
            />
            <button type="button" className={iconBtnCls} onClick={() => setFigure({ edges: removeAt(edges, i) })} aria-label={`Remove arrow ${i + 1}`}>
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ))}
        <button
          type="button"
          className={addBtnCls}
          disabled={nodes.length < 2}
          onClick={() => setFigure({ edges: [...edges, { from: nodes[0]?.id ?? 'n1', to: nodes[1]?.id ?? 'n2', label: null }] })}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add arrow
        </button>
      </fieldset>
      <TextArea
        label="Text equivalent (read out in order, 6.2 rule 12)"
        value={slide.text_equivalent}
        onChange={(text_equivalent) => onChange({ ...slide, text_equivalent })}
      />
    </div>
  );
}

function KnowledgeCheckEditor({ slide, onChange }: { slide: KnowledgeCheckSlide; onChange: (slide: KnowledgeCheckSlide) => void }) {
  return (
    <div className="space-y-3">
      <TextArea label="Question" value={slide.stem} onChange={(stem) => onChange({ ...slide, stem })} testId="lms-slide-stem" />
      <SelectField
        label="Answer type"
        value={slide.kind}
        onChange={(kind) => onChange({ ...slide, kind: kind as QuestionKind, options: optionsForKind(slide.options, kind as QuestionKind) })}
        options={QUESTION_KINDS.map((k) => ({ value: k, label: QUESTION_KIND_LABELS[k] }))}
      />
      <ChoiceOptionsEditor options={slide.options} kind={slide.kind} onChange={(options) => onChange({ ...slide, options })} testId="lms-slide-options" />
      <TextArea
        label="Explanation (shown after answering, whatever the answer)"
        value={slide.explanation}
        onChange={(explanation) => onChange({ ...slide, explanation })}
      />
    </div>
  );
}

function ScenarioEditor({ slide, onChange }: { slide: ScenarioSlide; onChange: (slide: ScenarioSlide) => void }) {
  return (
    <div className="space-y-3">
      <TextInput label="Role (who is in the situation)" value={slide.role} onChange={(role) => onChange({ ...slide, role })} />
      <TextArea label="Situation (markdown)" value={slide.situation_md} onChange={(situation_md) => onChange({ ...slide, situation_md })} markdown rows={4} />
      <TextInput label="Decision question" value={slide.question} onChange={(question) => onChange({ ...slide, question })} />
      <ScenarioOptionsEditor options={slide.options} onChange={(options) => onChange({ ...slide, options })} />
    </div>
  );
}

function LayoutFields({ slide, onChange }: SlideFormProps) {
  switch (slide.layout) {
    case 'title':
      return <TextArea label="Body (markdown)" value={slide.body_md} onChange={(body_md) => onChange({ ...slide, body_md })} markdown testId="lms-slide-body" />;
    case 'bullets':
      return (
        <StringList
          label="Bullets (inline markdown, at most 5 of 20 words each)"
          items={slide.bullets}
          onChange={(bullets) => onChange({ ...slide, bullets })}
          max={5}
          addLabel="Add bullet"
          itemName="Bullet"
          testId="lms-slide-bullets"
        />
      );
    case 'two_column':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <TextArea label="Left column (markdown)" value={slide.left_md} onChange={(left_md) => onChange({ ...slide, left_md })} markdown />
          <TextArea label="Right column (markdown)" value={slide.right_md} onChange={(right_md) => onChange({ ...slide, right_md })} markdown />
        </div>
      );
    case 'callout':
      return (
        <div className="space-y-3">
          <SelectField
            label="Tone"
            value={slide.tone}
            onChange={(tone) => onChange({ ...slide, tone: tone as CalloutTone })}
            options={CALLOUT_TONES.map((t) => ({ value: t, label: TONE_LABELS[t] }))}
            hint="6.2 rule 9: a must-do rule is marked Rule, a consequence Risk."
          />
          <TextArea label="Body (markdown)" value={slide.body_md} onChange={(body_md) => onChange({ ...slide, body_md })} markdown testId="lms-slide-body" />
        </div>
      );
    case 'steps': {
      const steps = Array.isArray(slide.steps) ? slide.steps : [];
      return (
        <fieldset className="space-y-2" data-testid="lms-steps-editor">
          <legend className={labelCls}>Steps (at most 5)</legend>
          {steps.map((step, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <div className="flex items-center justify-between gap-2">
                <span className={microLabelCls}>{`Step ${i + 1}`}</span>
                <RowActions
                  index={i}
                  count={steps.length}
                  label={`step ${i + 1}`}
                  onMove={(to) => onChange({ ...slide, steps: moveItem(steps, i, to) })}
                  onRemove={() => onChange({ ...slide, steps: removeAt(steps, i) })}
                  canRemove={steps.length > 1}
                />
              </div>
              <TextInput label="Step title" value={step.title} onChange={(title) => onChange({ ...slide, steps: replaceAt(steps, i, { ...step, title }) })} />
              <TextArea label="Step body (markdown)" value={step.body_md} onChange={(body_md) => onChange({ ...slide, steps: replaceAt(steps, i, { ...step, body_md }) })} markdown rows={2} />
            </div>
          ))}
          <button type="button" className={addBtnCls} disabled={steps.length >= 5} onClick={() => onChange({ ...slide, steps: [...steps, { title: '', body_md: '' }] })}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add step
          </button>
        </fieldset>
      );
    }
    case 'table':
      return <TableEditor slide={slide} onChange={onChange} />;
    case 'figure':
      return <FigureEditor slide={slide} onChange={onChange} />;
    case 'image':
      return (
        <div className="space-y-3">
          <AssetPicker
            assetId={slide.asset_id}
            onSelect={(asset) => onChange({ ...slide, asset_id: asset.id, alt: slide.alt.trim() ? slide.alt : asset.alt_default ?? '' })}
          />
          <TextInput
            label="Alt text (required)"
            value={slide.alt}
            onChange={(alt) => onChange({ ...slide, alt })}
            hint="What a screen reader says in place of the image (6.2 rule 12)."
            testId="lms-slide-alt"
          />
          <TextInput label="Caption" value={slide.caption ?? ''} onChange={(caption) => onChange({ ...slide, caption: caption || null })} />
        </div>
      );
    case 'scenario':
      return <ScenarioEditor slide={slide} onChange={onChange} />;
    case 'knowledge_check':
      return <KnowledgeCheckEditor slide={slide} onChange={onChange} />;
    default:
      return null;
  }
}

export function SlideForm({ slide, onChange }: SlideFormProps) {
  const headingValue = (slide as { heading?: string | null }).heading ?? '';
  const isCheck = slide.layout === 'knowledge_check';
  return (
    <div className="space-y-3" data-testid="lms-slide-form" data-slide-id={slide.id} data-layout={slide.layout}>
      <div className="flex flex-wrap items-end gap-3">
        <SelectField
          className="w-56"
          label="Layout"
          value={slide.layout}
          onChange={(layout) => onChange(blankSlide(layout as SlideLayout, slide.id, slide))}
          options={SLIDE_LAYOUTS.map((l) => ({ value: l, label: LAYOUT_LABELS[l] }))}
          testId="lms-slide-layout"
          hint="Changing the layout keeps the heading and notes and clears the rest."
        />
        <span className={`${hintCls} pb-2`}>{`Slide id ${slide.id}`}</span>
      </div>
      <TextInput
        label={isCheck ? 'Heading (optional)' : 'Heading'}
        value={headingValue}
        onChange={(heading) => onChange({ ...slide, heading: isCheck ? heading || null : heading } as Slide)}
        testId="lms-slide-heading"
      />
      <LayoutFields slide={slide} onChange={onChange} />
      <TextArea
        label="Speaker notes (a collapsible More line; never knowledge the quiz depends on)"
        value={slide.speaker_notes ?? ''}
        onChange={(notes) => onChange({ ...slide, speaker_notes: notes || null })}
        rows={2}
      />
    </div>
  );
}

/** The slide rows of a section: select one to edit, reorder, remove, add by layout. */
export function SlideList({
  slides,
  selected,
  onSelect,
  onChange,
  onAdd,
  errorCount,
}: {
  slides: Slide[];
  selected: number;
  onSelect: (index: number) => void;
  onChange: (slides: Slide[]) => void;
  onAdd: (layout: SlideLayout) => void;
  errorCount: (index: number) => number;
}) {
  return (
    <div className="space-y-2" data-testid="lms-slide-list">
      <div className={subheadingCls}>{`Slides (${slides.length})`}</div>
      <ol className="space-y-1">
        {slides.map((slide, i) => {
          const heading = (slide as { heading?: string | null }).heading;
          const label =
            slide.layout === 'knowledge_check'
              ? (slide as KnowledgeCheckSlide).stem || 'Knowledge check'
              : heading || `${LAYOUT_LABELS[slide.layout]} slide`;
          const count = errorCount(i);
          return (
            <li key={slide.id} className="flex items-center gap-2">
              <button
                type="button"
                className={`${i === selected ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'} flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 text-left text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-1.5`}
                onClick={() => onSelect(i)}
                aria-current={i === selected ? 'true' : undefined}
                data-testid={`lms-slide-row-${i}`}
              >
                <span className="w-6 flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400">{i + 1}</span>
                <span className="min-w-0 flex-1 truncate">{label}</span>
                <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">{LAYOUT_LABELS[slide.layout]}</span>
                {count > 0 ? (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900/40 dark:text-red-300">
                    {`${count} to fix`}
                  </span>
                ) : null}
              </button>
              <RowActions
                index={i}
                count={slides.length}
                label={`slide ${i + 1}`}
                onMove={(to) => {
                  onChange(moveItem(slides, i, to));
                  if (to >= 0 && to < slides.length) onSelect(to);
                }}
                onRemove={() => {
                  onChange(removeAt(slides, i));
                  onSelect(Math.max(0, Math.min(i, slides.length - 2)));
                }}
              />
            </li>
          );
        })}
      </ol>
      <AddSlide onAdd={onAdd} />
    </div>
  );
}

function AddSlide({ onAdd }: { onAdd: (layout: SlideLayout) => void }) {
  const id = useId();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="sr-only" htmlFor={id}>
        Layout of the new slide
      </label>
      <select
        id={id}
        defaultValue="bullets"
        data-testid="lms-add-slide-layout"
        className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0"
      >
        {SLIDE_LAYOUTS.map((l) => (
          <option key={l} value={l}>
            {LAYOUT_LABELS[l]}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={addBtnCls}
        data-testid="lms-add-slide"
        onClick={(e) => {
          const select = (e.currentTarget.parentElement?.querySelector('select') ?? null) as HTMLSelectElement | null;
          onAdd((select?.value as SlideLayout) || 'bullets');
        }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Add slide
      </button>
    </div>
  );
}

export default SlideForm;
